import React, { useState } from "react";
import { X, Plus, Trash2, Lock, AlertTriangle, ListTree, Pencil, RotateCcw, Copy } from "lucide-react";
import clsx from "clsx";
import { tasksApi, blockersApi, blocksApi } from "@/lib/api";
import { confirm } from "@/lib/dialog";
import { toast } from "sonner";

import FilesSection from "./FilesSection";
import NoteDialog from "./NoteDialog";
import CrewAssignmentsSection from "./CrewAssignmentsSection";
import AiHelpers from "./AiHelpers";
import RecurrenceSection from "./RecurrenceSection";

// Style maps — keep ternaries flat and readable.
const BLOCKER_BORDER = {
  critical: "border-red bg-red-glow",
  warning: "border-amber bg-amber-glow",
};
const BLOCKER_BORDER_DEFAULT = "border-line bg-bg-elevated";

const BLOCKER_ICON = {
  critical: "text-red",
  warning: "text-amber",
};
const BLOCKER_ICON_DEFAULT = "text-ink-mute";

const SEVERITY_ACTIVE = {
  critical: "border-red text-red bg-red-glow",
  warning: "border-amber text-amber bg-amber-glow",
  info: "border-cyan text-cyan bg-cyan-glow",
};
const SEVERITY_INACTIVE = "border-line text-ink-mute";

const SUBTASK_STATUS = {
  done: "text-ink-mute border-line line-through",
  in_progress: "text-amber border-amber bg-amber-glow",
  blocked: "text-red border-red bg-red-glow",
};
const SUBTASK_STATUS_DEFAULT = "text-ink-dim border-line";

// Pulls a useful, human-readable error message out of an axios error.
// Handles: string detail, FastAPI validation arrays, object detail with `.message`,
// network errors with no response, and falls back to a caller-supplied default.
function extractError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (detail && typeof detail === "object" && typeof detail.message === "string") return detail.message;
  if (err?.message && err.message !== "Network Error") return `${fallback} — ${err.message}`;
  if (err?.message === "Network Error") return "Network error — check your connection.";
  return fallback;
}

export default function TaskDetailDrawer({
  open, onClose, task, block, room, blockers, subtasks = [],
  crew = [], assignments = [], equipment = [], onMutated,
}) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [severity, setSeverity] = useState("warning");
  const [newSub, setNewSub] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);

  if (!open || !task) return null;
  const isSubtask = !!task.parent_task_id;

  const addBlocker = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await blockersApi.create(task.id, { reason: reason.trim(), severity });
      setReason("");
      onMutated && onMutated();
    } finally {
      setBusy(false);
    }
  };

  const resolveBlocker = async (b) => {
    await blockersApi.update(b.id, { resolved: true });
    onMutated && onMutated();
  };

  const toggleLock = async () => {
    setBusy(true);
    try {
      await tasksApi.update(task.id, { is_locked: !task.is_locked });
      onMutated && onMutated();
    } finally {
      setBusy(false);
    }
  };

  const addSubtask = async () => {
    if (!newSub.trim()) return;
    setBusy(true);
    try {
      await tasksApi.create(task.project_id, {
        title: newSub.trim(),
        parent_task_id: task.id,
        primary_room_id: task.primary_room_id || block?.room_id,
        duration_min: 30,
        status: "planned",
        priority: "normal",
      });
      setNewSub("");
      onMutated && onMutated();
    } finally {
      setBusy(false);
    }
  };

  const removeSubtask = async (s) => {
    const ok = await confirm({
      title: `Delete subtask "${s.title}"?`,
      body: "Soft-delete — recoverable from the audit log.",
      confirmLabel: "DELETE",
      danger: true,
    });
    if (!ok) return;
    try {
      await tasksApi.remove(s.id);
      onMutated && onMutated();
    } catch (e) {
      console.error("Subtask delete failed", e);
      toast.error(extractError(e, "Delete subtask failed"));
    }
  };

  const childSubtasks = subtasks.length;
  const childBlocks = !isSubtask && task.id ? 1 : 0; // current `block` if any
  const isRecurringMaster = !!task.is_recurring && !task.recurrence_parent_id;

  const duplicateTask = async () => {
    setBusy(true);
    try {
      await tasksApi.duplicate(task.id);
      toast.success(`Duplicated "${task.title}" — find it in FLOATING`);
      onMutated && onMutated();
    } catch (e) {
      console.error("Task duplicate failed", e);
      toast.error(extractError(e, "Duplicate failed"));
    } finally {
      setBusy(false);
    }
  };

  const deleteTask = async () => {
    const cascades = [];
    if (childSubtasks > 0) cascades.push(`${childSubtasks} subtask${childSubtasks === 1 ? "" : "s"}`);
    if (childBlocks > 0) cascades.push(`its scheduled block${childBlocks === 1 ? "" : "s"}`);
    if (isRecurringMaster) cascades.push("every materialised recurrence");
    const body = cascades.length
      ? `This will also delete ${cascades.join(", ")}.\n\nSoft-delete — recoverable from the audit log.`
      : "Soft-delete — recoverable from the audit log.";
    const ok = await confirm({
      title: `Delete "${task.title}"?`,
      body,
      confirmLabel: "DELETE",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await tasksApi.remove(task.id);
      toast.success(`Deleted "${task.title}"`);
      onMutated && onMutated();
      onClose && onClose();
    } catch (e) {
      console.error("Task delete failed", e);
      toast.error(extractError(e, "Delete failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="drawer animate-slide-in-right" data-testid="task-detail-drawer">
      <div className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-surface">
        <div className="min-w-0">
          <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">TASK</div>
          <div className="text-sm text-ink mt-0.5 truncate" title={task.title}>{task.title}</div>
          <div className="mono text-2xs text-ink-mute uppercase tracking-wider mt-0.5">
            {room?.abbreviation} · {task.status} · {task.priority}
          </div>
        </div>
        <button onClick={onClose} className="btn-ghost" data-testid="detail-close">
          <X className="w-3 h-3" />
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        {/* Actions */}
        <div className="p-3 border-b border-line flex gap-2 flex-wrap items-center">
          <button onClick={toggleLock} className="btn-ghost" disabled={busy} data-testid="toggle-lock-btn">
            <Lock className="w-3 h-3" />
            {task.is_locked ? "Unlock" : "Lock"}
          </button>
          <button onClick={duplicateTask} className="btn-ghost" disabled={busy || isSubtask} data-testid="duplicate-task-btn" title="Duplicate task — copies title/duration/notes but not crew or schedule">
            <Copy className="w-3 h-3" /> Duplicate
          </button>
          <div className="flex-1" />
          <button
            onClick={deleteTask}
            disabled={busy}
            className="text-red border border-red/40 hover:bg-red-glow hover:text-red-light px-2 py-1 mono text-2xs uppercase tracking-widest transition-colors disabled:opacity-50"
            data-testid="delete-task-btn"
            title="Delete this task (and any subtasks / scheduled blocks)"
          >
            <Trash2 className="w-3 h-3 inline mr-1" /> DELETE TASK
          </button>
        </div>

        {/* Crew Assignments on this block */}
        {!isSubtask && (
          <Section title="OWNER">
            <select
              value={task.owner_crew_id || ""}
              onChange={async (e) => {
                await tasksApi.update(task.id, { owner_crew_id: e.target.value || null });
                onMutated && onMutated();
              }}
              className="w-full bg-bg-elevated text-xs p-2 outline-none border border-line focus:border-amber"
              data-testid="task-owner-select"
            >
              <option value="">— none (no auto-assign) —</option>
              {crew.map((c) => (
                <option key={c.id} value={c.id}>{c.name} {c.role ? `· ${c.role}` : ""}</option>
              ))}
            </select>
            <p className="mono text-2xs text-ink-mute uppercase tracking-widest mt-1">
              Owner is auto-assigned as Lead when a block is created.
            </p>
          </Section>
        )}

        {!isSubtask && block && (
          <Section title={`CREW ON THIS BLOCK · ${assignments.length}`}>
            <CrewAssignmentsSection
              blockId={block.id}
              allCrew={crew}
              assignments={assignments}
              ownerCrewId={task.owner_crew_id}
              onMutated={onMutated}
            />
          </Section>
        )}

        {/* Reschedule remainder for in-progress tasks */}
        {!isSubtask && task.status === "in_progress" && block && (
          <Section title="RESCHEDULE REMAINDER">
            <RescheduleRemainder task={task} block={block} onMutated={onMutated} />
          </Section>
        )}

        {/* Notes */}
        <Section title="NOTES">
          <button
            type="button"
            onClick={() => setNoteOpen(true)}
            className="w-full text-left p-3 border border-line bg-bg-elevated hover:border-amber hover:bg-bg-highlight transition-colors group"
            data-testid="task-notes-edit-btn"
          >
            {task.notes ? (
              <div className="text-xs text-ink whitespace-pre-wrap leading-relaxed" data-testid="task-notes-display">
                {task.notes}
              </div>
            ) : (
              <div className="mono text-2xs text-ink-mute uppercase tracking-widest">
                — no notes —
              </div>
            )}
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute group-hover:text-amber mt-2 flex items-center gap-1">
              <Pencil className="w-2.5 h-2.5" />
              {task.notes ? "Click to edit" : "Click to add"}
            </div>
          </button>
        </Section>

        {/* Blockers */}
        <Section title="BLOCKERS">
          <div className="space-y-2">
            {(blockers || []).map((b) => (
              <div key={b.id} className={clsx(
                "flex items-start gap-2 p-2 border",
                BLOCKER_BORDER[b.severity] || BLOCKER_BORDER_DEFAULT
              )}>
                <AlertTriangle className={clsx(
                  "w-3 h-3 mt-0.5",
                  BLOCKER_ICON[b.severity] || BLOCKER_ICON_DEFAULT
                )} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-ink">{b.reason}</div>
                  <div className="mono text-2xs text-ink-mute uppercase tracking-wider mt-0.5">
                    {b.severity}{b.resolved_at && " · resolved"}
                  </div>
                </div>
                {!b.resolved_at && (
                  <button onClick={() => resolveBlocker(b)} className="btn-ghost text-2xs">
                    RESOLVE
                  </button>
                )}
              </div>
            ))}
            <div className="border border-line p-2">
              <div className="flex items-center gap-1 mb-2">
                {["info", "warning", "critical"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setSeverity(s)}
                    className={clsx(
                      "px-2 py-0.5 border mono text-2xs uppercase tracking-wider",
                      severity === s ? SEVERITY_ACTIVE[s] : SEVERITY_INACTIVE
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addBlocker(); }}
                placeholder="describe blocker…"
                className="w-full bg-bg-elevated text-xs p-1.5 outline-none border border-line focus:border-amber"
                data-testid="blocker-input"
              />
              <button onClick={addBlocker} disabled={busy || !reason.trim()} className="btn-primary mt-2 w-full justify-center">
                <Plus className="w-3 h-3" /> ADD BLOCKER
              </button>
            </div>
          </div>
        </Section>

        {/* Subtasks */}
        {!isSubtask && (
          <Section title={`SUBTASKS · ${subtasks.length}`}>
            {subtasks.length > 0 && (
              <div className="mb-2 p-2 bg-bg-elevated border border-line" data-testid="group-layout-toggle">
                <div className="mono text-2xs uppercase tracking-widest text-ink-mute mb-1.5">GROUP LAYOUT</div>
                <div className="flex gap-1">
                  {[
                    { v: "sequential", label: "SEQUENTIAL", hint: "back-to-back" },
                    { v: "parallel", label: "PARALLEL", hint: "all at once" },
                  ].map((opt) => {
                    const active = (task.group_layout || "sequential") === opt.v;
                    return (
                      <button
                        key={opt.v}
                        onClick={async () => {
                          if (active) return;
                          await tasksApi.update(task.id, { group_layout: opt.v });
                          onMutated && onMutated();
                        }}
                        className={clsx(
                          "flex-1 px-2 py-1.5 mono text-2xs uppercase tracking-widest border transition-colors",
                          active
                            ? "bg-amber text-bg-base border-amber"
                            : "bg-bg-base text-ink-mute border-line hover:border-amber hover:text-amber"
                        )}
                        data-testid={`group-layout-${opt.v}`}
                        title={opt.hint}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!task.display_collapsed}
                    onChange={async (e) => {
                      await tasksApi.update(task.id, { display_collapsed: e.target.checked });
                      onMutated && onMutated();
                    }}
                    className="accent-amber"
                    data-testid="group-collapsed-toggle"
                  />
                  <span className="mono text-2xs uppercase tracking-widest text-ink-mute">
                    SHOW AS SUMMARY BAR
                  </span>
                </label>
              </div>
            )}
            <div className="space-y-1">
              {subtasks.map((s) => (
                <div key={s.id} className="flex items-center gap-2 p-1.5 border border-line bg-bg-elevated">
                  <ListTree className="w-3 h-3 text-ink-mute shrink-0" />
                  <span className="text-xs text-ink truncate flex-1" title={s.title}>{s.title}</span>
                  <span className={clsx(
                    "mono text-2xs uppercase tracking-wider px-1.5 py-0.5 border",
                    SUBTASK_STATUS[s.status] || SUBTASK_STATUS_DEFAULT
                  )}>
                    {s.status}
                  </span>
                  <button
                    onClick={() => removeSubtask(s)}
                    className="text-ink-mute hover:text-red shrink-0"
                    title="Delete subtask"
                    data-testid={`subtask-delete-${s.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {subtasks.length === 0 && (
                <div className="mono text-2xs text-ink-mute uppercase tracking-widest py-1">
                  — no subtasks —
                </div>
              )}
              <div className="flex gap-1 mt-2">
                <input
                  value={newSub}
                  onChange={(e) => setNewSub(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addSubtask(); }}
                  placeholder="new subtask…"
                  className="flex-1 bg-bg-elevated text-xs p-1.5 outline-none border border-line focus:border-amber"
                  data-testid="subtask-input"
                />
                <button
                  onClick={addSubtask}
                  disabled={busy || !newSub.trim()}
                  className="btn-primary px-3"
                  data-testid="subtask-add-btn"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              <AiHelpers
                projectId={task.project_id}
                title={task.title}
                roomId={task.primary_room_id || block?.room_id}
                parentDurationMin={task.duration_min}
                onEstimateApplied={async (est) => {
                  await tasksApi.update(task.id, { duration_min: est.likely_minutes });
                  onMutated && onMutated();
                }}
                onSubtaskAdded={async (s) => {
                  await tasksApi.create(task.project_id, {
                    title: s.title,
                    parent_task_id: task.id,
                    primary_room_id: task.primary_room_id || block?.room_id,
                    duration_min: s.duration_min,
                    status: "planned",
                    priority: "normal",
                  });
                  onMutated && onMutated();
                }}
              />
            </div>
          </Section>
        )}

        {/* Recurrence (parent tasks only) */}
        {!isSubtask && (
          <Section title="RECURRENCE">
            <RecurrenceSection task={task} onMutated={onMutated} />
          </Section>
        )}

        {/* Equipment now lives at the room level — managed in the /equipment view. */}

        {/* Files */}
        <Section title="ATTACHMENTS">
          <FilesSection taskId={task.id} />
        </Section>

        {/* Estimates */}
        <Section title="ESTIMATES">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <EditableMinField
              label="MANUAL"
              value={task.estimate_manual_min}
              onSave={async (v) => { await tasksApi.update(task.id, { estimate_manual_min: v }); onMutated && onMutated(); }}
              testid="estimate-manual"
            />
            <Field label="ACTUAL" value={fmtMin(task.actual_min)} />
            <Field label="AI LIKELY" value={fmtMin(task.estimate_ai_min_likely)} />
            <Field label="AI MAX" value={fmtMin(task.estimate_ai_min_max)} />
          </div>
          <EditableCrewCount
            value={task.crew_count_estimate}
            onSave={async (v) => { await tasksApi.update(task.id, { crew_count_estimate: v }); onMutated && onMutated(); }}
          />
        </Section>
      </div>

      <NoteDialog
        open={noteOpen}
        title={task.title}
        initialValue={task.notes}
        placeholder="Add task notes — site requirements, special instructions, equipment quirks…"
        onSave={async (v) => {
          await tasksApi.update(task.id, { notes: v });
          onMutated && onMutated();
        }}
        onClose={() => setNoteOpen(false)}
      />
    </div>
  );
}

function EditableMinField({ label, value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const commit = async () => {
    const n = draft.trim() === "" ? null : parseInt(draft, 10);
    if (!isNaN(n) || n === null) {
      await onSave(n);
    }
    setEditing(false);
  };
  if (editing) {
    return (
      <div className="bg-bg-elevated p-2 border border-amber">
        <div className="mono text-2xs uppercase tracking-widest text-ink-mute">{label}</div>
        <input
          autoFocus
          type="number"
          min="0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          className="mono text-sm bg-transparent text-ink outline-none w-full mt-0.5"
          data-testid={`${testid}-input`}
          placeholder="min"
        />
      </div>
    );
  }
  return (
    <button
      onClick={() => { setDraft(value != null ? String(value) : ""); setEditing(true); }}
      className="bg-bg-elevated p-2 border border-line hover:border-amber text-left transition-colors"
      data-testid={`${testid}-btn`}
    >
      <div className="mono text-2xs uppercase tracking-widest text-ink-mute">{label}</div>
      <div className="mono text-sm text-ink mt-0.5 tabular-nums">{fmtMin(value)}</div>
    </button>
  );
}

function EditableCrewCount({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const commit = async () => {
    const n = draft.trim() === "" ? null : parseInt(draft, 10);
    if (!isNaN(n) || n === null) {
      await onSave(n);
    }
    setEditing(false);
  };
  return (
    <div className="bg-bg-elevated p-2 border border-line mt-2 flex items-center justify-between gap-2">
      <div>
        <div className="mono text-2xs uppercase tracking-widest text-ink-mute">CREW NEEDED</div>
        <div className="text-2xs text-ink-mute mt-0.5">Estimated number of crew for this task</div>
      </div>
      {editing ? (
        <input
          autoFocus
          type="number"
          min="0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          className="mono text-lg bg-transparent text-amber outline-none w-16 text-right border-b border-amber"
          data-testid="crew-count-input"
          placeholder="0"
        />
      ) : (
        <button
          onClick={() => { setDraft(value != null ? String(value) : ""); setEditing(true); }}
          className="mono text-lg text-ink tabular-nums hover:text-amber transition-colors"
          data-testid="crew-count-btn"
        >
          {value != null ? value : "—"}
        </button>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-b border-line">
      <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute px-3 pt-3 pb-1">{title}</div>
      <div className="px-3 pb-3">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-bg-elevated p-2 border border-line">
      <div className="mono text-2xs uppercase tracking-widest text-ink-mute">{label}</div>
      <div className="mono text-sm text-ink mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

function fmtMin(m) {
  if (m == null) return "—";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 0) return `${mm}m`;
  if (mm === 0) return `${h}h`;
  return `${h}h ${mm}m`;
}

/**
 * In-progress task: producer marks how many minutes are still needed.
 * - If "schedule now" picked → shrinks current block to elapsed minutes (rounded to 15),
 *   marks task done, and creates a NEW floating task for the remainder so the user can drop it.
 * - If "next day same time" picked → same, but the new task is scheduled in the same room
 *   exactly 24h after the original start.
 */
function RescheduleRemainder({ task, block, onMutated }) {
  const [remaining, setRemaining] = useState("");
  const [mode, setMode] = useState("float");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const n = parseInt(remaining, 10);
    if (isNaN(n) || n <= 0) {
      toast.error("Enter remaining minutes.");
      return;
    }
    setBusy(true);
    try {
      // 1) Compute elapsed: now - block.start_at, snapped to 15-min, min 15.
      const startMs = new Date(block.start_at).getTime();
      const elapsedRaw = Math.max(15, Math.round((Date.now() - startMs) / 60000));
      const elapsed = Math.max(15, Math.round(elapsedRaw / 15) * 15);
      const newEnd = new Date(startMs + elapsed * 60000).toISOString().replace(/\.\d+Z$/, "Z");

      // 2) Shrink current block + mark task done.
      await blocksApi.update(block.id, { end_at: newEnd });
      await tasksApi.update(task.id, {
        status: "done",
        actual_min: elapsed,
      });

      // 3) Create remainder task — floating or next-day-same-time.
      const remainder = await tasksApi.create(task.project_id, {
        title: `${task.title} — REMAINDER`,
        description: task.description || null,
        primary_room_id: task.primary_room_id || block.room_id,
        owner_crew_id: task.owner_crew_id || null,
        duration_min: n,
        crew_count_estimate: task.crew_count_estimate || null,
        priority: task.priority || "normal",
        is_floating: mode === "float",
        notes: `Rescheduled remainder of "${task.title}". Original block ${block.id}.`,
      });

      if (mode === "next_day") {
        const nextStart = new Date(startMs + 24 * 3600 * 1000);
        const nextEnd = new Date(nextStart.getTime() + n * 60000);
        await blocksApi.create(task.project_id, {
          task_id: remainder.id,
          room_id: block.room_id,
          start_at: nextStart.toISOString().replace(/\.\d+Z$/, "Z"),
          end_at: nextEnd.toISOString().replace(/\.\d+Z$/, "Z"),
          is_locked: false,
        });
      }

      toast.success(`Marked done · ${n}m queued ${mode === "float" ? "in floating tray" : "for next day"}.`);
      setRemaining("");
      onMutated && onMutated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reschedule remainder.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-2xs text-ink-mute leading-relaxed">
        Mark this block done and queue the remaining work.
      </p>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="15"
          step="15"
          value={remaining}
          onChange={(e) => setRemaining(e.target.value)}
          placeholder="min remaining"
          className="flex-1 bg-bg-elevated text-xs p-1.5 outline-none border border-line focus:border-amber mono tabular-nums"
          data-testid="remainder-min-input"
        />
        <span className="mono text-2xs text-ink-mute uppercase tracking-widest">MIN</span>
      </div>
      <div className="grid grid-cols-2 gap-1">
        {[
          { id: "float", label: "Float to tray" },
          { id: "next_day", label: "Next day, same time" },
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => setMode(opt.id)}
            className={clsx(
              "p-1.5 border mono text-2xs uppercase tracking-wider",
              mode === opt.id ? "border-amber text-amber bg-amber-glow" : "border-line text-ink-mute hover:border-ink-mute"
            )}
            data-testid={`remainder-mode-${opt.id}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <button
        onClick={submit}
        disabled={busy || !remaining}
        className="btn-primary w-full justify-center"
        data-testid="remainder-submit"
      >
        <RotateCcw className="w-3 h-3" /> {busy ? "Working…" : "Reschedule remainder"}
      </button>
    </div>
  );
}
